/**
 * กฎการ normalize และตรวจสอบเลขทะเบียนลูกค้า
 *
 * ใช้ตัวเดียวกันทั้งฝั่ง client (แสดงผลทันที) และฝั่ง server (เป็นตัวตัดสิน)
 * ค่าที่ควบคุมพฤติกรรมทั้งหมดมาจาก RegistrationSettings ไม่มีการ hard-code รูปแบบ
 */
import type { RegistrationSettings } from './config';
import { AppError, ERROR_CODES } from './errors';

export interface RegistrationCheck {
  valid: boolean;
  raw: string;
  normalized: string;
  /** เหตุผลภาษาไทยเมื่อ valid = false */
  reason: string | null;
}

/**
 * ตัดเฉพาะอักขระที่ "มองไม่เห็น" ออก — control character, soft hyphen,
 * zero-width, bidi mark/override, word joiner และ BOM
 * ซึ่งมักติดมาเวลาผู้ใช้ copy เลขทะเบียนจาก PDF หรือ Excel แล้วมองด้วยตาไม่เห็น
 *
 * สำคัญ: ต้องไม่ตัดอักขระที่มองเห็นได้ทิ้ง (เช่น ตัวอักษรไทย) เพราะถ้าตัด
 * ค่าที่ผู้ใช้พิมพ์ผิดจะกลายเป็นเลขทะเบียนอื่นที่ถูกต้องโดยไม่มีใครรู้ตัว
 * อักขระที่มองเห็นแต่ไม่อนุญาต ต้องให้ checkRegistration เป็นผู้ปฏิเสธแทน
 */
function stripInvisible(value: string): string {
  let out = '';
  for (const char of value) {
    const cp = char.codePointAt(0)!;
    if (cp <= 0x1f || cp === 0x7f) continue; // C0 controls + DEL
    if (cp >= 0x80 && cp <= 0x9f) continue; // C1 controls
    if (cp === 0xad) continue; // soft hyphen
    if (cp >= 0x200b && cp <= 0x200f) continue; // zero-width + bidi marks
    if (cp === 0x2028 || cp === 0x2029) continue; // line / paragraph separator
    if (cp >= 0x202a && cp <= 0x202e) continue; // bidi embedding + override
    if (cp >= 0x2060 && cp <= 0x2064) continue; // word joiner + invisible operators
    if (cp === 0xfeff) continue; // BOM / zero-width no-break space
    out += char;
  }
  return out;
}

/**
 * ทำให้เลขทะเบียนอยู่ในรูปมาตรฐานเดียวสำหรับเก็บลง column ที่ใช้ index และตรวจซ้ำ
 * ขั้นตอน: normalize unicode -> ตัดช่องว่างหัวท้าย -> ตัดอักขระที่มองไม่เห็น
 *          -> ตัดอักขระคั่นตาม stripPattern -> แปลงเป็นพิมพ์ใหญ่
 */
export function normalizeRegistration(input: string, settings: RegistrationSettings): string {
  let value = String(input ?? '')
    .normalize('NFKC')
    .trim();
  value = stripInvisible(value);

  if (settings.stripPattern) {
    try {
      value = value.replace(new RegExp(settings.stripPattern, 'g'), '');
    } catch {
      // stripPattern ที่ผู้ดูแลระบบตั้งผิดรูปแบบ ต้องไม่ทำให้บันทึกข้อมูลไม่ได้ทั้งระบบ
      // ข้ามขั้นตอนนี้ไป แล้วให้ checkRegistration เป็นผู้ปฏิเสธค่าที่ผิดรูปแบบแทน
    }
  }
  if (settings.uppercase) {
    value = value.toUpperCase();
  }
  return value;
}

export function checkRegistration(
  input: string,
  settings: RegistrationSettings,
): RegistrationCheck {
  const raw = String(input ?? '').trim();
  const normalized = normalizeRegistration(raw, settings);

  const fail = (reason: string): RegistrationCheck => ({ valid: false, raw, normalized, reason });

  if (normalized.length === 0) {
    return fail('กรุณากรอกเลขทะเบียนลูกค้า');
  }
  if (normalized.length < settings.minLength) {
    return fail(`เลขทะเบียนสั้นเกินไป ต้องมีอย่างน้อย ${settings.minLength} ตัวอักษร`);
  }
  if (normalized.length > settings.maxLength) {
    return fail(`เลขทะเบียนยาวเกินไป ต้องไม่เกิน ${settings.maxLength} ตัวอักษร`);
  }
  // อนุญาตเฉพาะตัวอักษรละตินและตัวเลขหลัง normalize เพื่อกันอักขระผิดรูปแบบและ homoglyph
  if (!/^[A-Za-z0-9]+$/.test(normalized)) {
    return fail('เลขทะเบียนมีอักขระที่ไม่อนุญาต ใช้ได้เฉพาะตัวเลขและตัวอักษรภาษาอังกฤษ');
  }
  if (settings.pattern) {
    let re: RegExp;
    try {
      re = new RegExp(settings.pattern);
    } catch {
      // pattern ที่ผู้ดูแลระบบตั้งผิด ไม่ควรทำให้ทั้งระบบบันทึกข้อมูลไม่ได้
      return { valid: true, raw, normalized, reason: null };
    }
    if (!re.test(normalized)) {
      return fail('เลขทะเบียนไม่ตรงรูปแบบที่หน่วยงานกำหนด');
    }
  }
  return { valid: true, raw, normalized, reason: null };
}

/** เวอร์ชันที่โยน AppError — ใช้ฝั่ง server */
export function assertRegistration(
  input: string,
  settings: RegistrationSettings,
): RegistrationCheck {
  const result = checkRegistration(input, settings);
  if (!result.valid) {
    throw new AppError(ERROR_CODES.REGISTRATION_INVALID, {
      message: result.reason ?? undefined,
      details: { minLength: settings.minLength, maxLength: settings.maxLength },
    });
  }
  return result;
}

/**
 * ปิดบังเลขทะเบียนบางส่วนสำหรับผู้ที่ไม่มีสิทธิ์เห็นข้อมูลเต็ม
 * เก็บ 2 ตัวหน้าและ 3 ตัวท้ายไว้ให้พอตรวจสอบย้อนหลังได้
 */
export function maskRegistration(normalized: string): string {
  if (normalized.length <= 5) return '*'.repeat(normalized.length);
  const head = normalized.slice(0, 2);
  const tail = normalized.slice(-3);
  return `${head}${'*'.repeat(normalized.length - 5)}${tail}`;
}
